import type { ReactNode } from 'react'

import type { FixtureQuery } from '../fixtures'

import { ErrorState, LoadingSkeleton } from './states'

/**
 * The four states every data screen owes its reader, in one place.
 *
 * Loading is a skeleton in the shape of the answer. Error carries the message,
 * the `requestId` and a Retry. Empty is the screen's own illustration, one line
 * and the primary action. Forbidden is not here on purpose — a route a role
 * cannot use is absent from navigation, so it is a routing decision, not a
 * rendering one.
 */
export function QueryBoundary<T>({
  query,
  skeleton,
  empty,
  isEmpty,
  children,
}: {
  query: FixtureQuery<T>
  skeleton?: ReactNode
  empty?: ReactNode
  isEmpty?: (data: T) => boolean
  children: (data: T) => ReactNode
}) {
  if (query.isError && query.error) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }

  if (query.data === undefined) {
    return <>{skeleton ?? <LoadingSkeleton variant="table" />}</>
  }

  if (empty && isEmpty?.(query.data)) return <>{empty}</>

  return <>{children(query.data)}</>
}
